/**
 * Quests Tab Content for GameMenu
 * Responsive design for all screen sizes
 * Features: Main/Side quest sections, reward tooltip, objective progress bars,
 *           quest completion celebration particles
 */

import { MenuTabContent } from '../MenuTabContent';
import { Player } from '@client/entities/Player';
import { Quest, MAIN_QUESTS } from '@shared/quests/Quest';
import { t } from '@locales/index';
import { formatNumber } from '@shared/utils';

interface CelebrationParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
}

export class QuestsTab extends MenuTabContent {
  private quests: Quest[] = [];
  private selectedQuestIndex: number = 0;
  private selectedTab: 'active' | 'available' | 'completed' = 'active';
  private selectedQuestType: 'main' | 'side' = 'main';
  private hoveredQuestIndex: number = -1;
  private trackedQuestId: string | null = null;
  private scrollOffset: number = 0;

  // Celebration particles for completed quests
  private celebrationParticles: CelebrationParticle[] = [];
  private celebrationTimer: number = 0;

  // Reward tooltip hover
  private tooltipQuest: Quest | null = null;
  private tooltipPosition: { x: number; y: number } = { x: 0, y: 0 };

  // Main quest IDs for classification
  private mainQuestIds: Set<string>;

  constructor(player: Player) {
    super(player);
    this.mainQuestIds = new Set(MAIN_QUESTS.map(q => q.id));
  }

  measureSize(_ctx: CanvasRenderingContext2D): { width: number; height: number } {
    return { width: 900, height: 550 };
  }

  update(deltaTime: number): void {
    // Update celebration particles
    this.celebrationTimer += deltaTime;
    this.celebrationParticles = this.celebrationParticles.filter(p => {
      p.x += p.vx * deltaTime;
      p.y += p.vy * deltaTime;
      p.vy += 30 * deltaTime; // Gravity
      p.life -= deltaTime;
      return p.life > 0;
    });
  }

  onActivate(): void {
    this.hoveredQuestIndex = -1;
    this.scrollOffset = 0;
    this.tooltipQuest = null;
  }

  updateQuests(quests: Quest[]): void {
    this.quests = quests;
  }

  getTrackedQuestId(): string | null {
    return this.trackedQuestId;
  }

  setTrackedQuestId(id: string | null): void {
    this.trackedQuestId = id;
  }

  /** Trigger celebration effect for a recently completed quest */
  triggerCelebration(_questId: string): void {
    this.celebrationTimer = 0;
    // Spawn sparkle particles around the details panel area
    const { x, y, width } = this.bounds;
    const layout = this.getLayout();
    const detailsX = x + layout.listWidth + layout.padding * 2;
    const detailsY = y + 85 * layout.scale;
    const detailsWidth = width - layout.listWidth - layout.padding * 3;
    const detailsCenterX = detailsX + detailsWidth / 2;
    const detailsCenterY = detailsY + 60 * layout.scale;

    const goldColors = ['#ffd700', '#ffec8b', '#fff8dc', '#ffcc00', '#daa520'];
    for (let i = 0; i < 30; i++) {
      const angle = (Math.PI * 2 * i) / 30 + Math.random() * 0.3;
      const speed = 40 + Math.random() * 80;
      this.celebrationParticles.push({
        x: detailsCenterX + (Math.random() - 0.5) * 60,
        y: detailsCenterY + (Math.random() - 0.5) * 30,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 40,
        life: 1.5 + Math.random() * 1.0,
        maxLife: 2.5,
        size: 2 + Math.random() * 3,
        color: goldColors[Math.floor(Math.random() * goldColors.length)],
      });
    }
  }

  // Get responsive layout
  private getLayout() {
    const { width, height } = this.bounds;
    const scale = Math.max(0.7, Math.min(1, width / 900));

    return {
      scale,
      padding: Math.max(12, 20 * scale),
      titleSize: Math.max(14, 20 * scale),
      largeSize: Math.max(12, 16 * scale),
      mediumSize: Math.max(10, 13 * scale),
      smallSize: Math.max(9, 11 * scale),
      tinySize: Math.max(8, 10 * scale),
      tabWidth: Math.max(80, 110 * scale),
      tabHeight: Math.max(24, 32 * scale),
      questTypeTabWidth: Math.max(60, 85 * scale),
      questTypeTabHeight: Math.max(20, 26 * scale),
      listWidth: width * 0.35,
      itemHeight: Math.max(40, 55 * scale),
      maxVisibleItems: Math.floor((height - 185 * scale) / (55 * scale)),
    };
  }

  private isMainQuest(quest: Quest): boolean {
    return this.mainQuestIds.has(quest.id);
  }

  private getFilteredQuests(): Quest[] {
    return this.quests.filter(q => {
      if (q.status !== this.selectedTab) return false;
      if (this.selectedQuestType === 'main') return this.isMainQuest(q);
      return !this.isMainQuest(q);
    });
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.bounds;
    const layout = this.getLayout();

    // Title
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${layout.titleSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText('GOREV GUNLUGU', x + layout.padding, y + 28 * layout.scale);

    // Status tabs (Active/Available/Completed)
    this.renderTabs(ctx, x, y, layout);

    // Main/Side quest type tabs
    this.renderQuestTypeTabs(ctx, x, y, layout);

    // Quest list (left side)
    this.renderQuestList(ctx, x, y, layout);

    // Quest details (right side)
    this.renderQuestDetails(ctx, x, y, width, height, layout);

    // Reward tooltip on hover
    this.renderRewardTooltip(ctx, layout);

    // Celebration particles
    this.renderCelebrationParticles(ctx);

    // Instructions
    ctx.fillStyle = '#505050';
    ctx.font = `${layout.tinySize}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(t('questLog.navHint'), x + width / 2, y + height - 8 * layout.scale);
  }

  private renderTabs(ctx: CanvasRenderingContext2D, x: number, y: number, layout: ReturnType<typeof this.getLayout>): void {
    const tabY = y + 45 * layout.scale;
    const tabsStartX = x + layout.padding;
    const tabSpacing = 6 * layout.scale;

    const tabs = [
      { id: 'active' as const, label: t('questLog.active'), count: this.quests.filter(q => q.status === 'active').length },
      { id: 'available' as const, label: t('questLog.available'), count: this.quests.filter(q => q.status === 'available').length },
      { id: 'completed' as const, label: t('questLog.completed'), count: this.quests.filter(q => q.status === 'completed').length }
    ];

    tabs.forEach((tab, i) => {
      const tabX = tabsStartX + i * (layout.tabWidth + tabSpacing);
      const isSelected = this.selectedTab === tab.id;

      // Tab background
      ctx.fillStyle = isSelected ? 'rgba(100, 70, 130, 0.8)' : 'rgba(50, 40, 60, 0.5)';
      this.drawRoundedRect(ctx, tabX, tabY, layout.tabWidth, layout.tabHeight, 5 * layout.scale);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = '#9966cc';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Tab label
      ctx.fillStyle = isSelected ? '#ffffff' : '#aaaaaa';
      ctx.font = isSelected ? `bold ${layout.smallSize}px Arial` : `${layout.smallSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(`${tab.label} (${tab.count})`, tabX + layout.tabWidth / 2, tabY + layout.tabHeight / 2 + 4 * layout.scale);
    });
  }

  private renderQuestTypeTabs(ctx: CanvasRenderingContext2D, x: number, y: number, layout: ReturnType<typeof this.getLayout>): void {
    const tabY = y + 80 * layout.scale;
    const tabsStartX = x + layout.padding;
    const tabSpacing = 4 * layout.scale;

    const mainCount = this.quests.filter(q => q.status === this.selectedTab && this.isMainQuest(q)).length;
    const sideCount = this.quests.filter(q => q.status === this.selectedTab && !this.isMainQuest(q)).length;

    const types = [
      { id: 'main' as const, label: t('questLog.main'), count: mainCount, color: '#ffd700' },
      { id: 'side' as const, label: t('questLog.side'), count: sideCount, color: '#88aacc' },
    ];

    types.forEach((tab, i) => {
      const tabX = tabsStartX + i * (layout.questTypeTabWidth + tabSpacing);
      const isSelected = this.selectedQuestType === tab.id;

      // Tab background
      ctx.fillStyle = isSelected ? 'rgba(70, 55, 90, 0.8)' : 'rgba(40, 35, 50, 0.4)';
      this.drawRoundedRect(ctx, tabX, tabY, layout.questTypeTabWidth, layout.questTypeTabHeight, 4 * layout.scale);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = tab.color;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Tab label
      ctx.fillStyle = isSelected ? tab.color : '#888888';
      ctx.font = `${isSelected ? 'bold ' : ''}${layout.tinySize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(`${tab.label} (${tab.count})`, tabX + layout.questTypeTabWidth / 2, tabY + layout.questTypeTabHeight / 2 + 3 * layout.scale);
    });
  }

  private renderQuestList(ctx: CanvasRenderingContext2D, x: number, y: number, layout: ReturnType<typeof this.getLayout>): void {
    const listX = x + layout.padding;
    const listY = y + 110 * layout.scale;
    const listWidth = layout.listWidth;

    const filteredQuests = this.getFilteredQuests();

    if (filteredQuests.length === 0) {
      ctx.fillStyle = '#666666';
      ctx.font = `${layout.mediumSize}px Arial`;
      ctx.textAlign = 'left';
      const noQuestMsg = this.selectedQuestType === 'side' ? t('questLog.noSideQuests') : t('questLog.noQuests');
      ctx.fillText(noQuestMsg, listX + 10 * layout.scale, listY + 25 * layout.scale);
      return;
    }

    for (let i = 0; i < filteredQuests.length && i < layout.maxVisibleItems; i++) {
      const quest = filteredQuests[i + this.scrollOffset];
      if (!quest) break;

      const itemY = listY + i * layout.itemHeight;
      const isSelected = i + this.scrollOffset === this.selectedQuestIndex;
      const isHovered = i + this.scrollOffset === this.hoveredQuestIndex;
      const isTracked = quest.id === this.trackedQuestId;

      // Item background
      ctx.fillStyle = isSelected ? 'rgba(80, 60, 100, 0.8)' : isHovered ? 'rgba(60, 50, 70, 0.6)' : 'rgba(40, 30, 50, 0.4)';
      this.drawRoundedRect(ctx, listX, itemY, listWidth, layout.itemHeight - 4 * layout.scale, 5 * layout.scale);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = '#8866aa';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Act indicator + quest type badge
      const isSide = !this.isMainQuest(quest);
      ctx.fillStyle = isSide ? '#88aacc' : this.getActColor(quest.act);
      ctx.font = `bold ${layout.tinySize}px Arial`;
      ctx.textAlign = 'left';
      ctx.fillText(isSide ? 'YAN' : `ACT ${quest.act}`, listX + 8 * layout.scale, itemY + 14 * layout.scale);

      // Quest name
      ctx.fillStyle = isTracked ? '#ffcc00' : '#ffffff';
      ctx.font = isSelected ? `bold ${layout.smallSize}px Arial` : `${layout.smallSize}px Arial`;

      let questName = quest.name;
      const maxChars = Math.floor(listWidth / (layout.smallSize * 0.6));
      if (questName.length > maxChars - 5) {
        questName = questName.substring(0, maxChars - 8) + '...';
      }
      ctx.fillText(questName, listX + 8 * layout.scale, itemY + 30 * layout.scale);

      // Progress indicator for active quests
      if (quest.status === 'active') {
        const progress = this.getQuestProgress(quest);

        // Progress bar
        const barWidth = 45 * layout.scale;
        const barX = listX + listWidth - barWidth - 8 * layout.scale;
        const barY = itemY + 26 * layout.scale;
        ctx.fillStyle = '#333333';
        ctx.fillRect(barX, barY, barWidth, 5 * layout.scale);
        ctx.fillStyle = '#66aa66';
        ctx.fillRect(barX, barY, barWidth * progress, 5 * layout.scale);

        ctx.fillStyle = '#888888';
        ctx.font = `${layout.tinySize}px Arial`;
        ctx.textAlign = 'right';
        ctx.fillText(`${Math.round(progress * 100)}%`, barX - 4 * layout.scale, barY + 5 * layout.scale);
      }

      // Completion badge for completed quests
      if (quest.status === 'completed') {
        ctx.fillStyle = '#66aa66';
        ctx.font = `bold ${layout.tinySize}px Arial`;
        ctx.textAlign = 'right';
        ctx.fillText('BITTI', listX + listWidth - 8 * layout.scale, itemY + 30 * layout.scale);
      }

      // Tracked indicator
      if (isTracked) {
        ctx.fillStyle = '#ffcc00';
        ctx.font = `${layout.smallSize}px Arial`;
        ctx.textAlign = 'right';
        ctx.fillText('*', listX + listWidth - 8 * layout.scale, itemY + 15 * layout.scale);
      }
    }

    // Scroll indicators
    if (this.scrollOffset > 0) {
      ctx.fillStyle = '#888888';
      ctx.font = `${layout.mediumSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('^', listX + listWidth / 2, listY - 5 * layout.scale);
    }
    if (this.scrollOffset + layout.maxVisibleItems < filteredQuests.length) {
      ctx.fillStyle = '#888888';
      ctx.font = `${layout.mediumSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('v', listX + listWidth / 2, listY + layout.maxVisibleItems * layout.itemHeight + 10 * layout.scale);
    }
  }

  private renderQuestDetails(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, layout: ReturnType<typeof this.getLayout>): void {
    const detailsX = x + layout.listWidth + layout.padding * 2;
    const detailsY = y + 85 * layout.scale;
    const detailsWidth = width - layout.listWidth - layout.padding * 3;
    const detailsHeight = height - 130 * layout.scale;

    // Details background
    ctx.fillStyle = 'rgba(30, 25, 40, 0.6)';
    this.drawRoundedRect(ctx, detailsX, detailsY, detailsWidth, detailsHeight, 8 * layout.scale);
    ctx.fill();

    const filteredQuests = this.getFilteredQuests();
    const quest = filteredQuests[this.selectedQuestIndex];

    if (!quest) {
      ctx.fillStyle = '#666666';
      ctx.font = `${layout.mediumSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(t('questLog.noSelection'), detailsX + detailsWidth / 2, detailsY + detailsHeight / 2);
      return;
    }

    const padding = 15 * layout.scale;
    const lineHeight = 20 * layout.scale;
    let yOffset = detailsY + 20 * layout.scale;

    // Quest name
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${layout.largeSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(quest.name, detailsX + padding, yOffset);
    yOffset += lineHeight * 1.2;

    // Act, chapter, and quest type
    const isSide = !this.isMainQuest(quest);
    ctx.fillStyle = isSide ? '#88aacc' : this.getActColor(quest.act);
    ctx.font = `${layout.smallSize}px Arial`;
    const typeLabel = isSide ? 'Yan Gorev' : `Act ${quest.act} - Bolum ${quest.chapter}`;
    ctx.fillText(typeLabel, detailsX + padding, yOffset);
    yOffset += lineHeight * 1.1;

    // Description
    ctx.fillStyle = '#cccccc';
    ctx.font = `${layout.smallSize}px Arial`;
    this.wrapText(ctx, quest.description, detailsX + padding, yOffset, detailsWidth - padding * 2, lineHeight * 0.85);
    yOffset += lineHeight * 2.5;

    // Objectives with per-objective progress bars
    ctx.fillStyle = '#aaaaaa';
    ctx.font = `bold ${layout.mediumSize}px Arial`;
    ctx.fillText(t('questLog.objectives'), detailsX + padding, yOffset);
    yOffset += lineHeight * 0.9;

    for (const objective of quest.objectives) {
      if (yOffset > detailsY + detailsHeight - 85 * layout.scale) break;

      const isComplete = objective.completed;
      ctx.fillStyle = isComplete ? '#66aa66' : '#ffffff';
      ctx.font = `${layout.smallSize}px Arial`;

      const checkMark = isComplete ? '>' : 'o';
      const progressText = objective.type === 'reach_zone' || objective.type === 'talk_to_npc'
        ? ''
        : ` (${objective.current}/${objective.required})`;

      ctx.fillText(`${checkMark} ${objective.description}${progressText}`, detailsX + padding + 5 * layout.scale, yOffset);
      yOffset += lineHeight * 0.6;

      // Per-objective progress bar (for kill/collect objectives with required > 1)
      if (objective.type !== 'reach_zone' && objective.type !== 'talk_to_npc' && objective.required > 1) {
        const barX = detailsX + padding + 15 * layout.scale;
        const barWidth = detailsWidth - padding * 2 - 20 * layout.scale;
        const barHeight = 6 * layout.scale;
        const progress = Math.min(1, objective.current / objective.required);

        // Bar background
        ctx.fillStyle = 'rgba(50, 50, 50, 0.8)';
        this.drawRoundedRect(ctx, barX, yOffset, barWidth, barHeight, 2 * layout.scale);
        ctx.fill();

        // Bar fill
        if (progress > 0) {
          const fillColor = isComplete ? '#66aa66' : '#5588cc';
          ctx.fillStyle = fillColor;
          this.drawRoundedRect(ctx, barX, yOffset, barWidth * progress, barHeight, 2 * layout.scale);
          ctx.fill();
        }

        // Progress label on bar
        ctx.fillStyle = '#ffffff';
        ctx.font = `${layout.tinySize - 1}px Arial`;
        ctx.textAlign = 'right';
        ctx.fillText(`${objective.current}/${objective.required}`, barX + barWidth - 2 * layout.scale, yOffset + barHeight - 1 * layout.scale);
        ctx.textAlign = 'left';

        yOffset += barHeight + 4 * layout.scale;
      } else {
        yOffset += lineHeight * 0.3;
      }
    }

    yOffset += lineHeight * 0.4;

    // Rewards section
    if (yOffset < detailsY + detailsHeight - 50 * layout.scale) {
      ctx.fillStyle = '#aaaaaa';
      ctx.font = `bold ${layout.mediumSize}px Arial`;
      ctx.textAlign = 'left';
      ctx.fillText('Oduller:', detailsX + padding, yOffset);
      yOffset += lineHeight * 0.85;

      ctx.font = `${layout.smallSize}px Arial`;
      if (quest.rewards.exp) {
        ctx.fillStyle = '#88ccff';
        ctx.fillText(`- ${quest.rewards.exp} Deneyim`, detailsX + padding + 5 * layout.scale, yOffset);
        yOffset += lineHeight * 0.75;
      }
      if (quest.rewards.gold) {
        ctx.fillStyle = '#ffcc44';
        ctx.fillText(`- ${formatNumber(quest.rewards.gold)} ${t('ui.gold')}`, detailsX + padding + 5 * layout.scale, yOffset);
        yOffset += lineHeight * 0.75;
      }
      if (quest.rewards.skillPoints) {
        ctx.fillStyle = '#66ff66';
        ctx.fillText(`- ${quest.rewards.skillPoints} Yetenek Puani`, detailsX + padding + 5 * layout.scale, yOffset);
        yOffset += lineHeight * 0.75;
      }
      if (quest.rewards.passivePoints) {
        ctx.fillStyle = '#ff66ff';
        ctx.fillText(`- ${quest.rewards.passivePoints} Pasif Puani`, detailsX + padding + 5 * layout.scale, yOffset);
        yOffset += lineHeight * 0.75;
      }
      if (quest.rewards.itemIds && quest.rewards.itemIds.length > 0) {
        ctx.fillStyle = '#ff8844';
        ctx.fillText(`- ${quest.rewards.itemIds.length} Esya Odulu`, detailsX + padding + 5 * layout.scale, yOffset);
      }
    }

    // Track button hint for active quests
    if (quest.status === 'active') {
      const isTracked = quest.id === this.trackedQuestId;
      ctx.fillStyle = isTracked ? '#ffcc00' : '#888888';
      ctx.font = `${layout.tinySize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(
        isTracked ? '* Takip Ediliyor - [T] ile kaldir' : '[T] ile Takip Et',
        detailsX + detailsWidth / 2,
        detailsY + detailsHeight - 12 * layout.scale
      );
    }

    // Completed badge
    if (quest.status === 'completed') {
      ctx.fillStyle = '#66aa66';
      ctx.font = `bold ${layout.mediumSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('-- TAMAMLANDI --', detailsX + detailsWidth / 2, detailsY + detailsHeight - 12 * layout.scale);
    }
  }

  /** Render reward tooltip when hovering a quest in the list */
  private renderRewardTooltip(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    if (!this.tooltipQuest) return;

    const quest = this.tooltipQuest;
    const tx = this.tooltipPosition.x;
    const ty = this.tooltipPosition.y;

    // Calculate tooltip size
    let lineCount = 1; // Title line
    if (quest.rewards.exp) lineCount++;
    if (quest.rewards.gold) lineCount++;
    if (quest.rewards.skillPoints) lineCount++;
    if (quest.rewards.passivePoints) lineCount++;
    if (quest.rewards.itemIds && quest.rewards.itemIds.length > 0) lineCount++;

    const tooltipWidth = 160 * layout.scale;
    const tooltipLineHeight = 16 * layout.scale;
    const tooltipHeight = (lineCount * tooltipLineHeight) + 12 * layout.scale;
    const tooltipPadding = 8 * layout.scale;

    // Position tooltip to the right of mouse, clamp to bounds
    let tooltipX = tx + 15;
    let tooltipY = ty - tooltipHeight / 2;
    if (tooltipX + tooltipWidth > this.bounds.x + this.bounds.width) {
      tooltipX = tx - tooltipWidth - 10;
    }
    if (tooltipY < this.bounds.y) tooltipY = this.bounds.y + 5;
    if (tooltipY + tooltipHeight > this.bounds.y + this.bounds.height) {
      tooltipY = this.bounds.y + this.bounds.height - tooltipHeight - 5;
    }

    ctx.save();

    // Tooltip background
    ctx.fillStyle = 'rgba(15, 10, 25, 0.95)';
    this.drawRoundedRect(ctx, tooltipX, tooltipY, tooltipWidth, tooltipHeight, 5 * layout.scale);
    ctx.fill();
    ctx.strokeStyle = '#8866aa';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    let yOff = tooltipY + tooltipPadding + 10 * layout.scale;

    // Title
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${layout.tinySize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText('Oduller:', tooltipX + tooltipPadding, yOff);
    yOff += tooltipLineHeight;

    ctx.font = `${layout.tinySize}px Arial`;

    if (quest.rewards.exp) {
      ctx.fillStyle = '#88ccff';
      ctx.fillText(`${quest.rewards.exp} XP`, tooltipX + tooltipPadding, yOff);
      yOff += tooltipLineHeight;
    }
    if (quest.rewards.gold) {
      ctx.fillStyle = '#ffcc44';
      ctx.fillText(`${formatNumber(quest.rewards.gold)} ${t('ui.gold')}`, tooltipX + tooltipPadding, yOff);
      yOff += tooltipLineHeight;
    }
    if (quest.rewards.skillPoints) {
      ctx.fillStyle = '#66ff66';
      ctx.fillText(`${quest.rewards.skillPoints} Yetenek Puan`, tooltipX + tooltipPadding, yOff);
      yOff += tooltipLineHeight;
    }
    if (quest.rewards.passivePoints) {
      ctx.fillStyle = '#ff66ff';
      ctx.fillText(`${quest.rewards.passivePoints} Pasif Puan`, tooltipX + tooltipPadding, yOff);
      yOff += tooltipLineHeight;
    }
    if (quest.rewards.itemIds && quest.rewards.itemIds.length > 0) {
      ctx.fillStyle = '#ff8844';
      ctx.fillText(`${quest.rewards.itemIds.length} Esya`, tooltipX + tooltipPadding, yOff);
    }

    ctx.restore();
  }

  /** Render gold sparkle celebration particles for completed quests */
  private renderCelebrationParticles(ctx: CanvasRenderingContext2D): void {
    if (this.celebrationParticles.length === 0) return;

    ctx.save();
    for (const p of this.celebrationParticles) {
      const alpha = Math.min(1, p.life / (p.maxLife * 0.3));
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.color;

      // Draw a small sparkle shape
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();

      // Inner bright core
      ctx.globalAlpha = alpha * 0.7;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * 0.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  handleMouseEvent(e: import('../components/UIComponent').MouseEvent): boolean {
    if (!this.visible) return false;

    const { x, y } = this.bounds;
    const layout = this.getLayout();
    const listX = x + layout.padding;
    const listY = y + 110 * layout.scale;
    const listWidth = layout.listWidth;
    const filteredQuests = this.getFilteredQuests();

    if (e.type === 'move') {
      this.hoveredQuestIndex = -1;
      this.tooltipQuest = null;

      // Check quest list hover
      for (let i = 0; i < Math.min(filteredQuests.length, layout.maxVisibleItems); i++) {
        const itemY = listY + i * layout.itemHeight;
        if (e.x >= listX && e.x <= listX + listWidth &&
            e.y >= itemY && e.y <= itemY + layout.itemHeight - 4 * layout.scale) {
          this.hoveredQuestIndex = i + this.scrollOffset;
          // Set tooltip quest and position for reward preview
          const hQuest = filteredQuests[i + this.scrollOffset];
          if (hQuest && hQuest.rewards) {
            this.tooltipQuest = hQuest;
            this.tooltipPosition = { x: e.x, y: e.y };
          }
          break;
        }
      }
    }

    if (e.type === 'down' && e.button === 'left') {
      // Check status tab clicks
      const tabY = y + 45 * layout.scale;
      const tabsStartX = x + layout.padding;
      const tabSpacing = 6 * layout.scale;

      const tabs: ('active' | 'available' | 'completed')[] = ['active', 'available', 'completed'];
      for (let i = 0; i < tabs.length; i++) {
        const tabX = tabsStartX + i * (layout.tabWidth + tabSpacing);
        if (e.x >= tabX && e.x <= tabX + layout.tabWidth &&
            e.y >= tabY && e.y <= tabY + layout.tabHeight) {
          this.selectedTab = tabs[i];
          this.selectedQuestIndex = 0;
          this.scrollOffset = 0;
          return true;
        }
      }

      // Check quest type tab clicks (Main/Side)
      const typeTabY = y + 80 * layout.scale;
      const typeTabSpacing = 4 * layout.scale;
      const questTypes: ('main' | 'side')[] = ['main', 'side'];
      for (let i = 0; i < questTypes.length; i++) {
        const tabX = tabsStartX + i * (layout.questTypeTabWidth + typeTabSpacing);
        if (e.x >= tabX && e.x <= tabX + layout.questTypeTabWidth &&
            e.y >= typeTabY && e.y <= typeTabY + layout.questTypeTabHeight) {
          this.selectedQuestType = questTypes[i];
          this.selectedQuestIndex = 0;
          this.scrollOffset = 0;
          return true;
        }
      }

      // Check quest list clicks
      for (let i = 0; i < Math.min(filteredQuests.length, layout.maxVisibleItems); i++) {
        const itemY = listY + i * layout.itemHeight;
        if (e.x >= listX && e.x <= listX + listWidth &&
            e.y >= itemY && e.y <= itemY + layout.itemHeight - 4 * layout.scale) {
          this.selectedQuestIndex = i + this.scrollOffset;
          return true;
        }
      }
    }

    // Handle scroll
    if (e.type === 'wheel' && e.deltaY) {
      const maxScroll = Math.max(0, filteredQuests.length - layout.maxVisibleItems);
      if (e.deltaY > 0) {
        this.scrollOffset = Math.min(this.scrollOffset + 1, maxScroll);
      } else {
        this.scrollOffset = Math.max(this.scrollOffset - 1, 0);
      }
      return true;
    }

    return false;
  }

  handleKeyDown(key: string): boolean {
    const filteredQuests = this.getFilteredQuests();
    const layout = this.getLayout();

    switch (key) {
      case 'ArrowUp':
        this.selectedQuestIndex = Math.max(0, this.selectedQuestIndex - 1);
        if (this.selectedQuestIndex < this.scrollOffset) {
          this.scrollOffset = this.selectedQuestIndex;
        }
        return true;

      case 'ArrowDown':
        this.selectedQuestIndex = Math.min(filteredQuests.length - 1, this.selectedQuestIndex + 1);
        if (this.selectedQuestIndex >= this.scrollOffset + layout.maxVisibleItems) {
          this.scrollOffset = this.selectedQuestIndex - layout.maxVisibleItems + 1;
        }
        return true;

      case 't':
      case 'T':
        if (filteredQuests[this.selectedQuestIndex]) {
          const quest = filteredQuests[this.selectedQuestIndex];
          if (quest.status === 'active') {
            this.trackedQuestId = this.trackedQuestId === quest.id ? null : quest.id;
          }
        }
        return true;

      case '1':
        this.selectedTab = 'active';
        this.selectedQuestIndex = 0;
        this.scrollOffset = 0;
        return true;

      case '2':
        this.selectedTab = 'available';
        this.selectedQuestIndex = 0;
        this.scrollOffset = 0;
        return true;

      case '3':
        this.selectedTab = 'completed';
        this.selectedQuestIndex = 0;
        this.scrollOffset = 0;
        return true;

      case '4':
        this.selectedQuestType = 'main';
        this.selectedQuestIndex = 0;
        this.scrollOffset = 0;
        return true;

      case '5':
        this.selectedQuestType = 'side';
        this.selectedQuestIndex = 0;
        this.scrollOffset = 0;
        return true;
    }

    return false;
  }

  private getQuestProgress(quest: Quest): number {
    let totalRequired = 0;
    let totalCurrent = 0;

    for (const obj of quest.objectives) {
      totalRequired += obj.required;
      totalCurrent += obj.current;
    }

    return totalRequired > 0 ? totalCurrent / totalRequired : 0;
  }

  private getActColor(act: number): string {
    switch (act) {
      case 1: return '#66aa66';
      case 2: return '#6688cc';
      case 3: return '#aa66aa';
      case 4: return '#cc6666';
      default: return '#888888';
    }
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number): void {
    const words = text.split(' ');
    let line = '';
    let currentY = y;

    for (const word of words) {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxWidth && line !== '') {
        ctx.fillText(line.trim(), x, currentY);
        line = word + ' ';
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }

    if (line.trim()) {
      ctx.fillText(line.trim(), x, currentY);
    }
  }
}
